import React, { useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { navTabs } from '../mock';
import { useLang } from '../i18n';
import Introduction from './sections/Introduction';
import Services from './sections/Services';
import Process from './sections/Process';
import Expertise from './sections/Expertise';
import Projects from './sections/Projects';
import Global from './sections/Global';
import Contact from './sections/Contact';

const TabbedContent = ({ activeTab, onTabChange, deepProjectSlug }) => {
  const { t } = useLang();
  const anchorRef = useRef(null);

  const goContact = () => onTabChange('contact');

  const render = () => {
    switch (activeTab) {
      case 'introduction': return <Introduction />;
      case 'capabilities': return <Services />;
      case 'process': return <Process />;
      case 'expertise': return <Expertise onContact={goContact} />;
      case 'projects': return <Projects initialSlug={deepProjectSlug} />;
      case 'global': return <Global />;
      case 'contact': return <Contact />;
      default: return <Introduction />;
    }
  };

  const handleTab = (id) => {
    onTabChange(id);
    // keep the content region in view
    if (anchorRef.current) {
      const top = anchorRef.current.getBoundingClientRect().top + window.scrollY - 90;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <section className="section-teal grid-overlay" data-testid="tabbed-content">
      <div ref={anchorRef} className="max-w-[1680px] mx-auto px-6 md:px-10 pt-10 pb-32 md:pb-28 relative">
        {/* Pill tab bar */}
        <div className="hidden lg:flex justify-start mb-14">
          <div className="relative inline-flex flex-wrap gap-1 rounded-full p-1.5 liquid-glass-bar">
            {/* top sheen reflection */}
            <span
              className="pointer-events-none absolute inset-x-2 top-0 h-1/2 rounded-full"
              style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0) 100%)' }}
            />
            {navTabs.map((tab) => (
              <button
                key={tab.id}
                data-testid={`pill-${tab.id}`}
                onClick={() => handleTab(tab.id)}
                className={`relative z-10 label-mono px-4 md:px-5 py-2.5 rounded-full transition-colors ${
                  activeTab === tab.id ? 'text-white' : 'text-white/60 hover:text-white'
                }`}
              >
                {activeTab === tab.id && (
                  <motion.span
                    layoutId="pill-bg"
                    className="absolute inset-0 rounded-full liquid-glass-pill"
                    transition={{ type: 'spring', stiffness: 400, damping: 34 }}
                  />
                )}
                <span className="relative z-10 drop-shadow-[0_1px_1px_rgba(0,0,0,0.35)]">{t(`nav.${tab.id}`)}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Section content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.45, ease: [0.4, 0, 0.2, 1] }}
          >
            {render()}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default TabbedContent;
